import React, { useState, useEffect, useRef } from 'react';
import { Modal, tokens } from '../design-system';

const JiraExportProgress = ({
  isOpen,
  onClose,
  exportId,
  onSuccess,
  onError
}) => {
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState('');
  const [currentMessage, setCurrentMessage] = useState('Initializing export...');
  const [isComplete, setIsComplete] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [resultData, setResultData] = useState(null);

  // Use refs for callbacks to avoid re-triggering the effect
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);
  useEffect(() => {
    onSuccessRef.current = onSuccess;
    onErrorRef.current = onError;
  }, [onSuccess, onError]);

  useEffect(() => {
    if (!isOpen) return;

    // If no exportId yet, wait for it
    if (!exportId) {
      setCurrentMessage('Initializing export...');
      return;
    }

    // Reset state when modal opens
    setProgress(0);
    setCurrentStep('');
    setCurrentMessage('Connecting to export service...');
    setIsComplete(false);
    setHasError(false);
    setErrorMessage('');
    setResultData(null);

    // Set up WebSocket connection for progress updates
    const wsBase = process.env.REACT_APP_WS_URL || `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}`;
    const ws = new WebSocket(`${wsBase}/ws/jira-export/${encodeURIComponent(exportId)}`);

    ws.onopen = () => {
      setCurrentMessage('Connected to export service...');
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        if (data.type === 'connection.established') {
          setCurrentMessage('Export service connected, starting export...');
        } else if (data.type === 'jira.export.progress') {
          const { step, progress: newProgress, message } = data.payload;

          setCurrentStep(step);
          setProgress(newProgress);
          setCurrentMessage(message);

          if (step === 'completion' && newProgress === 100) {
            setIsComplete(true);
          }
        } else if (data.type === 'jira.export.success') {
          setResultData(data.payload);
          setIsComplete(true);
          setProgress(100);
          setCurrentMessage('Export completed successfully!');

          // Notify parent immediately (e.g. to update badge) but do NOT auto-close.
          // The user dismisses the modal themselves after reviewing results.
          if (onSuccessRef.current) {
            onSuccessRef.current(data.payload);
          }
        } else if (data.type === 'jira.export.error') {
          setHasError(true);
          setErrorMessage(data.payload.message || 'Export failed');

          if (onErrorRef.current) {
            onErrorRef.current(data.payload);
          }
        }
      } catch (error) {
        console.error('Error parsing WebSocket message:', error);
      }
    };

    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
      setHasError(true);
      setErrorMessage('Connection error during export');
    };

    ws.onclose = () => {
    };

    // Cleanup on unmount or when modal closes
    return () => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.close();
      }
    };
  }, [isOpen, exportId]);

  const getStepDescription = (step) => {
    switch (step) {
      case 'project_creation': return 'Creating Project';
      case 'feature_creation': return 'Creating Features';
      case 'story_creation': return 'Creating Stories';
      case 'completion': return 'Complete';
      default: return 'Processing';
    }
  };

  const getStepIcon = (step) => {
    if (hasError) return '\u274C';
    if (isComplete) return '\u2705';
    switch (step) {
      case 'project_creation': return '\uD83C\uDFD7\uFE0F';
      case 'feature_creation': return '\uD83D\uDCCB';
      case 'story_creation': return '\uD83D\uDCD6';
      case 'completion': return '\uD83C\uDF89';
      default: return '\u2699\uFE0F';
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={(isComplete || hasError) ? onClose : undefined}
      title={
        <span style={{ display: 'flex', alignItems: 'center', gap: tokens.spacing[3] }}>
          JIRA Export Progress
          <span style={{ fontSize: tokens.typography.fontSize['2xl'][0] }}>{getStepIcon(currentStep)}</span>
        </span>
      }
      size="sm"
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacing[6] }}>
        {hasError ? (
          <div style={progressStyles.errorBox}>
            <div style={{ display: 'flex', gap: tokens.spacing[3] }}>
              <span style={{ flexShrink: 0 }}>{'\u274C'}</span>
              <div>
                <h3 style={progressStyles.alertTitle}>Export Failed</h3>
                <p style={{ ...progressStyles.alertText, marginTop: tokens.spacing[1] }}>{errorMessage}</p>
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Progress Bar */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: tokens.spacing[2] }}>
                <span style={progressStyles.stepLabel}>{getStepDescription(currentStep)}</span>
                <span style={progressStyles.percentLabel}>{progress}%</span>
              </div>
              <div style={progressStyles.progressTrack}>
                <div style={{
                  ...progressStyles.progressBar,
                  width: `${progress}%`,
                  backgroundColor: isComplete ? tokens.colors.success[500] : tokens.colors.primary[500],
                }} />
              </div>
            </div>

            {/* Current Status */}
            <div style={progressStyles.statusBox}>
              <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacing[3] }}>
                {!isComplete && !hasError && <div style={progressStyles.spinner} />}
                <span style={progressStyles.statusText}>{currentMessage}</span>
              </div>
            </div>

            {/* Success Message */}
            {isComplete && resultData && (
              <div style={progressStyles.successBox}>
                <div style={{ display: 'flex', gap: tokens.spacing[3] }}>
                  <span style={{ flexShrink: 0 }}>{'\u2705'}</span>
                  <div>
                    <h3 style={progressStyles.alertTitle}>Export Completed!</h3>
                    <p style={{ ...progressStyles.alertText, marginTop: tokens.spacing[1] }}>
                      Created {resultData.features_created > 0 ? `${resultData.features_created} features and ` : ''}{resultData.stories_created} stories in project {resultData.project_key}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* Action Buttons */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: tokens.spacing[3] }}>
          {isComplete && resultData?.project_url && (
            <a href={resultData.project_url} target="_blank" rel="noopener noreferrer" style={progressStyles.primaryBtn}>
              Open JIRA Project
            </a>
          )}
          <button
            onClick={onClose}
            style={{ ...progressStyles.closeBtn, ...((isComplete || hasError) ? {} : progressStyles.disabledBtn) }}
            disabled={!isComplete && !hasError}
          >
            {isComplete || hasError ? 'Close' : 'Exporting...'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

const progressStyles = {
  stepLabel: { fontSize: tokens.typography.fontSize.sm[0], fontWeight: tokens.typography.fontWeight.medium, color: 'var(--color-text-muted)' },
  percentLabel: { fontSize: tokens.typography.fontSize.sm[0], color: 'var(--color-text-muted)' },
  progressTrack: { width: '100%', height: '12px', backgroundColor: 'var(--color-bg-secondary)', borderRadius: '9999px', overflow: 'hidden' },
  progressBar: { height: '100%', borderRadius: '9999px', transition: 'width 0.3s ease, background-color 0.3s ease' },
  statusBox: { padding: tokens.spacing[4], backgroundColor: 'var(--color-bg-secondary)', borderRadius: tokens.borderRadius.md },
  statusText: { fontSize: tokens.typography.fontSize.sm[0], color: 'var(--color-text-muted)' },
  spinner: { width: '16px', height: '16px', border: '2px solid transparent', borderTopColor: tokens.colors.primary[500], borderRadius: '50%', animation: 'spin 0.8s linear infinite', flexShrink: 0 },
  errorBox: { padding: tokens.spacing[4], backgroundColor: 'rgba(239, 68, 68, 0.1)', border: `1px solid ${tokens.colors.error[500]}`, borderRadius: tokens.borderRadius.md },
  successBox: { padding: tokens.spacing[4], backgroundColor: 'rgba(34, 197, 94, 0.1)', border: `1px solid ${tokens.colors.success[500]}`, borderRadius: tokens.borderRadius.md },
  alertTitle: { margin: 0, fontSize: tokens.typography.fontSize.sm[0], fontWeight: tokens.typography.fontWeight.medium, color: 'var(--color-text)' },
  alertText: { margin: 0, fontSize: tokens.typography.fontSize.sm[0], color: 'var(--color-text-muted)' },
  primaryBtn: { padding: `${tokens.spacing[2]} ${tokens.spacing[4]}`, fontSize: tokens.typography.fontSize.sm[0], fontFamily: tokens.typography.fontFamily.sans.join(', '), color: '#fff', backgroundColor: tokens.colors.primary[500], border: 'none', borderRadius: tokens.borderRadius.md, cursor: 'pointer', textDecoration: 'none', display: 'inline-block' },
  closeBtn: { padding: `${tokens.spacing[2]} ${tokens.spacing[4]}`, fontSize: tokens.typography.fontSize.sm[0], fontFamily: tokens.typography.fontFamily.sans.join(', '), color: 'var(--color-text-muted)', backgroundColor: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)', borderRadius: tokens.borderRadius.md, cursor: 'pointer' },
  disabledBtn: { opacity: 0.5, cursor: 'not-allowed' },
};

if (typeof document !== 'undefined' && !document.getElementById('export-spinner-keyframes')) {
  const style = document.createElement('style');
  style.id = 'export-spinner-keyframes';
  style.textContent = '@keyframes spin { to { transform: rotate(360deg); } }';
  document.head.appendChild(style);
}

export default JiraExportProgress;
